import { MouseEvent, useContext, useState } from 'react';

import { Edit, Share, Visible, Hidden } from '@digital-hig/icon';
import { Popover, SvgIcon, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';

import I18nContext from '../../../aurr/contexts/I18nContext';
import ShareUI from '../share/Share';

interface CollectionRow {
  eyebrow?: string;
  timestamp?: string | number;
  title: string;
  editLink?: string;
  description: string;
  url: string;
  isPublic: boolean;
}
interface Props {
  entries: CollectionRow[];
  langCode: string;
}

const getDateFromTimestamp = (
  timestamp: number | string,
  locale = 'default'
) => {
  const date = new Date(Number(timestamp) * 1000);
  const year = date.getFullYear();
  const month = date.toLocaleString(locale, { month: 'long' });
  const day = date.getDate();
  return `${month} ${day}, ${year}`;
};

function CollectionsTable({ entries, langCode }: Props) {
  const { t } = useContext(I18nContext);

  const [anchorEl, setAnchorEl] = useState<null | (EventTarget & HTMLElement)>(
    null
  );

  if (!entries?.length) {
    return null;
  }

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const removeHtmlTags = (str: string): string => {
    return str.replace(/<[^>]*>/g, '');
  };

  return (
    <div className="au-border-t au-border-gray-e5 au-flex au-flex-col au-gap-c15 lg:au-gap-0">
      {entries.map(entry => {
        return (
          <div
            className="au-border au-border-gray-99 lg:au-border-l-0 lg:au-border-r-0 lg:au-border-t-0 au-text-white au-font-element au-p-c15 au-flex au-flex-col au-gap-c15 lg:au-flex-row lg:au-items-center lg:au-justify-between lg:au-gap-0"
            key={entry.title + entry.url}
          >
            <div className="au-flex au-flex-row au-gap-c15 lg:au-flex-col lg:au-gap-0">
              <div className="au-flex-1 au-min-w-0 lg:au-mb-0">
                {(entry.timestamp || entry.eyebrow) && (
                  <div className="au-text-c12 au-capitalize au-mb-c8">
                    {!!entry.eyebrow && entry.eyebrow}
                    {!entry.eyebrow &&
                      entry.timestamp &&
                      getDateFromTimestamp(entry?.timestamp, langCode)}
                  </div>
                )}
                {!!entry.title && (
                  <a
                    href={entry.url}
                    className="au-block au-text-white au-text-c14 au-leading-c14 au-font-semibold au-mb-c10 au-decoration-none au-break-words"
                  >
                    {entry.title}
                  </a>
                )}
                {!!entry.description && (
                  <div className="au-text-c14 au-italic au-break-words">
                    {entry.description}
                  </div>
                )}
              </div>
              <div className="au-flex au-flex-col au-shrink-0 au-items-end au-gap-c8 au-justify-start lg:au-flex-row lg:au-flex-wrap lg:au-items-center lg:au-justify-end lg:au-gap-c10">
                <Tooltip
                  title={removeHtmlTags(
                    t(
                      `${
                        entry.isPublic
                          ? 'playlists.shareEveryone'
                          : 'playlists.shareLinkOnly'
                      }`,
                      { ns: 'common' }
                    )
                  )}
                  arrow
                >
                  <SvgIcon fontSize="medium" className="au-cursor-pointer">
                    {entry.isPublic ? (
                      <Visible color="white" />
                    ) : (
                      <Hidden color="white" />
                    )}
                  </SvgIcon>
                </Tooltip>
                <Button
                  onClick={handleClick}
                  startIcon={
                    <SvgIcon fontSize="medium">
                      <Share />
                    </SvgIcon>
                  }
                  variant="text"
                  className="!au-text-white lg:!au-text-c14 !au-text-c12 lg:!au-px-c12 !au-px-c8"
                  size="small"
                  sx={{ fontSize: { lg: '14px' } }}
                >
                  {t('Share', { ns: 'common' })}
                </Button>
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <div className="au-p-c25">
                    <ShareUI
                      title={entry.title}
                      networks={['email', 'linkedin', 'twitter', 'facebook']}
                    />
                  </div>
                </Popover>
                {!!entry.editLink && (
                  <Button
                    href={entry.editLink}
                    startIcon={
                      <SvgIcon fontSize="medium">
                        <Edit />
                      </SvgIcon>
                    }
                    variant="text"
                    className="!au-text-white lg:!au-text-c14 !au-text-c12 lg:!au-px-c12 !au-px-c8"
                    size="small"
                    sx={{ fontSize: { lg: '14px' } }}
                  >
                    {t('Update', { ns: 'common' })}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CollectionsTable;
